<template>
  <div class="w-full flex flex-col gap-1">
    <div class="flex justify-between items-center w-full">
      <span class="font-medium">מידע נוסף</span>
      <span
        v-if="fieldDefinitions.length"
        class="underline font-medium cursor-pointer"
        @click="openNewField"
      >+ שדה חדש</span>
    </div>
    <span class="text-xs text-gray-500">שדות שהגדרתם לעסק — נשמרים לכל לקוח, עם תיעוד שינויים לאורך זמן.</span>

    <div
      v-if="!fieldDefinitions.length"
      class="border border-dashed border-gray-300 rounded-xl mt-2 px-4 py-6 flex flex-col items-center text-center gap-1"
    >
      <span class="rounded-full p-2.5 bg-primary-100 flex mb-1">
        <i class="pi pi-sliders-h text-primary-700" />
      </span>
      <span class="font-medium">שמרו מידע משלכם על הלקוח</span>
      <span class="text-xs text-gray-500 max-w-64">הגדירו שדות משלכם — כמו עוצמת מכונת לייזר, מספר טיפולים או סוג עור — ותעדו כל שינוי.</span>
      <Button label="הוספת שדה ראשון" icon="pi pi-plus" rounded size="small" class="mt-3" @click="openNewField" />
    </div>

    <div v-else class="flex flex-col gap-2 mt-2">
      <div
        v-for="field in fieldDefinitions"
        :key="field.id"
        class="border rounded-xl p-3 bg-white"
      >
        <div class="flex justify-between items-start">
          <div class="flex flex-col">
            <span class="text-sm font-medium">{{ field.label }}</span>
            <template v-if="currentEntry(field)">
              <span class="flex items-baseline gap-1 mt-1">
                <span class="text-2xl font-bold leading-none">{{ currentEntry(field)!.value }}</span>
                <span v-if="field.unit" class="text-sm text-gray-500">{{ field.unit }}</span>
              </span>
              <span class="flex items-center gap-2 mt-1">
                <span class="text-[11px] text-gray-400">נכון ל-{{ formatDate(currentEntry(field)!.date) }}</span>
                <i
                  class="pi pi-pencil text-gray-400 cursor-pointer !text-xs"
                  title="עריכת הערך"
                  @click="openEditEntry(field, currentEntry(field)!, 0)"
                />
                <i
                  class="pi pi-trash text-gray-400 cursor-pointer !text-xs"
                  title="מחיקת הערך"
                  @click="confirmRemoveEntry(field, currentEntry(field)!, 0)"
                />
              </span>
              <span v-if="currentEntry(field)!.note" class="text-xs text-gray-500 mt-1">{{ currentEntry(field)!.note }}</span>
            </template>
            <span v-else class="text-sm text-gray-400 mt-1">— טרם עודכן</span>
          </div>
          <div class="flex items-center gap-3 flex-none">
            <span class="underline font-medium cursor-pointer" @click="openEditValue(field)">עדכון</span>
            <i
              class="pi pi-trash text-gray-400 cursor-pointer !text-sm"
              title="הסרת שדה"
              @click="confirmRemoveField(field)"
            />
          </div>
        </div>

        <div v-if="field.trackHistory && historyOf(field).length > 1" class="mt-2 pt-2 border-t">
          <div
            class="flex items-center gap-1 text-xs text-gray-500 cursor-pointer select-none"
            @click="toggleHistory(field.id)"
          >
            <i class="pi text-[10px]" :class="openHistories[field.id] ? 'pi-chevron-down' : 'pi-chevron-left'" />
            <span>היסטוריית שינויים ({{ historyOf(field).length }})</span>
          </div>
          <div v-if="openHistories[field.id]" class="flex flex-col mt-2">
            <div
              v-for="(entry, index) in historyOf(field)"
              :key="index"
              class="flex gap-2.5 py-1.5"
            >
              <span class="w-1.5 h-1.5 rounded-full flex-none mt-1.5" :class="index === 0 ? 'bg-primary-500' : 'bg-gray-300'" />
              <div class="flex flex-col flex-1 min-w-0">
                <div class="flex items-center gap-2">
                  <span class="text-sm" :class="index === 0 ? 'font-medium' : ''">{{ entry.value }}{{ field.unit ? ` ${field.unit}` : '' }}</span>
                  <span class="ms-auto text-xs text-gray-400 flex-none">{{ formatDate(entry.date) }}</span>
                  <template v-if="index > 0">
                    <i
                      class="pi pi-pencil text-gray-400 cursor-pointer !text-xs flex-none"
                      title="עריכת הערך"
                      @click="openEditEntry(field, entry, index)"
                    />
                    <i
                      class="pi pi-trash text-gray-400 cursor-pointer !text-xs flex-none"
                      title="מחיקת הערך"
                      @click="confirmRemoveEntry(field, entry, index)"
                    />
                  </template>
                </div>
                <span v-if="entry.note" class="text-xs text-gray-500 mt-0.5 break-words">{{ entry.note }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, PropType, ref } from 'vue';
import {
  ICustomer,
  ICustomerCustomFieldValue,
  ICustomFieldDefinition,
  ICustomFieldHistoryEntry
} from '@torim-app/types';
import { DateTime } from 'luxon';
import { useToast } from 'vue-toastification';
import { useConfirm } from 'primevue/useconfirm';
import { useDrawer } from '@torim-app/shared-components';
import { useAdminStore } from '@app/stores';
import { CustomersApi } from '@app/api/admin';
import AddCustomField from '@app/components/admin/modals/AddCustomField.vue';
import EditCustomFieldValue from '@app/components/admin/modals/EditCustomFieldValue.vue';
import { trackEvent } from '@app/composables/posthog';

const toast = useToast();
const confirm = useConfirm();
const adminStore = useAdminStore();
const { openDrawer, closeDrawer } = useDrawer();

const emit = defineEmits(['updated']);
const props = defineProps({
  customer: {
    type: Object as PropType<ICustomer>,
    required: true
  }
});

const openHistories = ref<Record<string, boolean>>({});

// Removed fields are only hidden (their values stay in the DB for support), so they are
// filtered out everywhere in the UI.
const fieldDefinitions = computed<Array<ICustomFieldDefinition>>(
  () => (adminStore.businessSiteData?.customerCustomFields ?? []).filter(field => !field.isHidden)
);

const valuesByFieldId = computed<Record<string, ICustomerCustomFieldValue>>(() => {
  const map: Record<string, ICustomerCustomFieldValue> = {};
  for (const fieldValue of props.customer?.customFields ?? [])
    map[fieldValue.fieldId] = fieldValue;
  return map;
});

const historyOf = (field: ICustomFieldDefinition): Array<ICustomFieldHistoryEntry> =>
  valuesByFieldId.value[field.id]?.history ?? [];

const currentEntry = (field: ICustomFieldDefinition): ICustomFieldHistoryEntry | undefined =>
  historyOf(field)[0];

const formatDate = (date: Date | string) =>
  DateTime.fromISO(String(date)).toLocaleString(DateTime.DATE_FULL);

const toggleHistory = (fieldId: string) => {
  openHistories.value[fieldId] = !openHistories.value[fieldId];
};

const openNewField = () => {
  openDrawer(AddCustomField, {
    onSave: async (definition: Omit<ICustomFieldDefinition, 'id'>) => {
      trackEvent('customer_custom_field_created');
      const updatedFields = await CustomersApi.createCustomFieldDefinition(definition);
      if (adminStore.businessSiteData)
        adminStore.businessSiteData.customerCustomFields = updatedFields;
      toast.success('השדה נוצר בהצלחה');
    },
    onClose: closeDrawer
  }, {
    title: 'שדה חדש',
    closable: true
  });
};

const openEditValue = (field: ICustomFieldDefinition) => {
  openDrawer(EditCustomFieldValue, {
    field,
    initialValue: currentEntry(field)?.value ?? '',
    onSave: async (payload: { value: string; date: Date; note?: string }) => {
      trackEvent('customer_custom_field_value_updated', {
        has_note: !!payload.note,
        is_back_dated: payload.date.toDateString() !== new Date().toDateString()
      });
      await CustomersApi.updateCustomFieldValue({
        customerId: props.customer.id!,
        fieldId: field.id,
        value: payload.value,
        date: payload.date.toISOString(),
        note: payload.note
      });
      toast.success('הערך עודכן בהצלחה');
      emit('updated');
    },
    onClose: closeDrawer
  }, {
    title: `עדכון · ${field.label}`,
    closable: true
  });
};

// Editing / removing one recorded value of this customer only — the field definition and
// the other customers' values are untouched.
const openEditEntry = (
  field: ICustomFieldDefinition,
  entry: ICustomFieldHistoryEntry,
  index: number
) => {
  openDrawer(EditCustomFieldValue, {
    field,
    entry,
    onSave: async (payload: { value: string; date: Date; note?: string }) => {
      trackEvent('customer_custom_field_entry_edited', { field_type: field.type });
      await CustomersApi.updateCustomFieldEntry({
        customerId: props.customer.id!,
        fieldId: field.id,
        // Entries recorded before per-entry editing existed have no id — the backend then
        // falls back to the position they are displayed at.
        entryId: entry.id,
        entryIndex: index,
        value: payload.value,
        date: payload.date.toISOString(),
        note: payload.note
      });
      toast.success('הערך עודכן בהצלחה');
      emit('updated');
    },
    onClose: closeDrawer
  }, {
    title: `עריכת ערך · ${field.label}`,
    closable: true
  });
};

const confirmRemoveEntry = (
  field: ICustomFieldDefinition,
  entry: ICustomFieldHistoryEntry,
  index: number
) => {
  confirm.require({
    header: 'מחיקת ערך',
    message: `למחוק את הערך "${entry.value}" מ-${formatDate(entry.date)}? הפעולה משפיעה על הלקוח הזה בלבד.`,
    acceptLabel: 'מחיקה',
    rejectLabel: 'ביטול',
    acceptClass: 'p-button-danger',
    blockScroll: true,
    accept: () => removeEntry(field, entry, index)
  });
};

const removeEntry = async (
  field: ICustomFieldDefinition,
  entry: ICustomFieldHistoryEntry,
  index: number
) => {
  try {
    await CustomersApi.removeCustomFieldEntry({
      customerId: props.customer.id!,
      fieldId: field.id,
      entryId: entry.id,
      entryIndex: index
    });
    trackEvent('customer_custom_field_entry_removed', { field_type: field.type });
    toast.success('הערך נמחק בהצלחה');
    emit('updated');
  } catch (e) {
    console.error(e);
    toast.error('לא הצלחנו למחוק את הערך');
  }
};

const confirmRemoveField = (field: ICustomFieldDefinition) => {
  confirm.require({
    header: 'הסרת שדה',
    message: `להסיר את השדה "${field.label}"? השדה יוסתר מכרטיסי כל הלקוחות בעסק, כולל לקוחות שכבר מילאו אותו.`,
    acceptLabel: 'הסרה',
    rejectLabel: 'ביטול',
    acceptClass: 'p-button-danger',
    blockScroll: true,
    accept: () => removeField(field)
  });
};

const removeField = async (field: ICustomFieldDefinition) => {
  try {
    const updatedFields = await CustomersApi.hideCustomFieldDefinition(field.id);
    if (adminStore.businessSiteData)
      adminStore.businessSiteData.customerCustomFields = updatedFields;
    trackEvent('customer_custom_field_removed', { field_type: field.type });
    toast.success('השדה הוסר בהצלחה');
  } catch (e) {
    console.error(e);
    toast.error('לא הצלחנו להסיר את השדה');
  }
};
</script>
